import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSalesModal as useGlobalSalesModal } from '../../../hooks/useSalesModal';
import { formatCurrency } from '../../../utils/formatters/money';
import { formatDate } from '../../../utils/formatters/date';
import type { SalesOrderCompletedPayload } from '../SalesStep3/SalesStep3.hooks';
import {
  SALES_CUSTOMER_STEP_ENABLED,
  BRANCH_RECEIPT_NUMBERING_ENABLED,
} from '../../../config/feature-flags';
import { salesService } from '../../../services/sales.service';
import {
  buildPrintChromeHtml,
  escapeHtml,
  openPrintWindow,
  PRINT_NO_PRINT_CSS,
  PRINT_POPUP_BLOCKED_MESSAGE,
} from '../../../utils/printWindow';

export const useSalesModal = () => {
  const totalSteps = SALES_CUSTOMER_STEP_ENABLED ? 4 : 3;
  const { state, isOpen, closeModal, calculateOrderSummary, finish } = useGlobalSalesModal();
  const {
    currentStep,
    selectedLocation,
    selectedProducts,
    selectedCustomer,
    mode,
    editingOrderCode,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    discountConfig,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    paymentConfig,
  } = state;

  // Local state for the modal
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [branchReceiptNumber, setBranchReceiptNumber] = useState<string | null>(null);
  const [tentativeBranchReceiptCode, setTentativeBranchReceiptCode] = useState<string | null>(null);
  /** True when the allocated receipt differs from the tentative one shown (concurrent sale). */
  const [branchReceiptChanged, setBranchReceiptChanged] = useState(false);
  // Last tentative code shown in the header, read at completion time (state is cleared then).
  const lastTentativeReceiptRef = useRef<string | null>(null);
  useEffect(() => {
    if (tentativeBranchReceiptCode != null) {
      lastTentativeReceiptRef.current = tentativeBranchReceiptCode;
    }
  }, [tentativeBranchReceiptCode]);
  const [orderCompletedAt, setOrderCompletedAt] = useState('');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const wasOpenRef = useRef(false);

  // Reset local UI flags whenever the drawer freshly opens (fixes stale confirm/success).
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setShowCancelConfirmation(false);
      setIsCompleted(false);
      setOrderNumber('');
      setBranchReceiptNumber(null);
      setTentativeBranchReceiptCode(null);
      setBranchReceiptChanged(false);
      lastTentativeReceiptRef.current = null;
      setOrderCompletedAt('');
      setShowPrintModal(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // Peek while arming the sale; re-fetch on last step so the folio is current before confirm.
  const isLastStep = currentStep === totalSteps;
  useEffect(() => {
    const locationId = selectedLocation?.id;
    const shouldPeek =
      BRANCH_RECEIPT_NUMBERING_ENABLED &&
      isOpen &&
      !isCompleted &&
      mode !== 'edit' &&
      locationId != null;

    if (!shouldPeek) {
      setTentativeBranchReceiptCode(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const code = await salesService.peekNextBranchReceiptCode(locationId);
        if (!cancelled) {
          setTentativeBranchReceiptCode(code);
        }
      } catch {
        if (!cancelled) {
          setTentativeBranchReceiptCode(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Refresh when location changes, and again when entering/leaving the confirmation step.
  }, [isOpen, isCompleted, mode, selectedLocation?.id, isLastStep]);

  // Handle order completion from SalesStep3
  const handleOrderCompleted = useCallback((payload: SalesOrderCompletedPayload) => {
    setOrderNumber(payload.orderCode);
    const allocated = payload.branchReceiptCode ?? null;
    const tentative = lastTentativeReceiptRef.current;
    setBranchReceiptNumber(allocated);
    setBranchReceiptChanged(allocated != null && tentative != null && allocated !== tentative);
    setTentativeBranchReceiptCode(null);
    setOrderCompletedAt(payload.completedAt);
    setIsCompleted(true);
  }, []);

  // Handle modal close (internal - used when confirmed)
  const handleClose = useCallback(() => {
    if (!isProcessing) {
      closeModal();
      setShowCancelConfirmation(false);
      setIsCompleted(false);
      setOrderNumber('');
      setBranchReceiptNumber(null);
      setTentativeBranchReceiptCode(null);
      setBranchReceiptChanged(false);
      lastTentativeReceiptRef.current = null;
      setOrderCompletedAt('');
      setShowPrintModal(false);
    }
  }, [isProcessing, closeModal]);

  // Handle close click (show confirmation if there are changes)
  const handleCloseClick = useCallback(() => {
    if (!isProcessing && !isCompleted) {
      // Mostrar confirmación si hay cambios (no está completado ni procesando)
      setShowCancelConfirmation(true);
    } else {
      // Si está completado o procesando, cerrar sin confirmación
      handleClose();
    }
  }, [isProcessing, isCompleted, handleClose]);

  // Cancel operation (close modal after confirmation)
  const cancelOperation = useCallback(() => {
    setShowCancelConfirmation(false);
    closeModal();
    setIsCompleted(false);
    setOrderNumber('');
    setBranchReceiptNumber(null);
    setTentativeBranchReceiptCode(null);
    setOrderCompletedAt('');
    setShowPrintModal(false);
  }, [closeModal]);

  // Handle print receipt
  const handlePrintReceipt = useCallback(() => {
    setShowPrintModal(true);
  }, []);

  // Handle print
  const handlePrint = useCallback(() => {
    const printContent = document.getElementById('receipt-content');
    if (!printContent) return;

    const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8"/>
    <title>Comprobante de Venta - ${escapeHtml(orderNumber)}</title>
    <style>
      body { font-family: 'Courier New', monospace; margin: 0; padding: 20px; background: white; }
      .receipt { max-width: 300px; margin: 0 auto; }
      .text-center { text-align: center; }
      .text-right { text-align: right; }
      .border-top { border-top: 1px dashed #000; margin: 10px 0; padding-top: 10px; }
      .font-bold { font-weight: bold; }
      .text-lg { font-size: 18px; }
      .text-sm { font-size: 12px; }
      .mb-2 { margin-bottom: 8px; }
      .mb-4 { margin-bottom: 16px; }
      .flex { display: flex; justify-content: space-between; }
      .w-full { width: 100%; }
      @media print {
        body { margin: 0; padding: 10px; }
      }
      ${PRINT_NO_PRINT_CSS}
    </style>
  </head>
  <body>
    ${printContent.innerHTML}
    ${buildPrintChromeHtml()}
  </body>
</html>`;

    const printWindow = openPrintWindow({
      html,
      size: 'ticket',
      windowName: 'printSalesReceipt',
    });

    if (!printWindow) {
      window.alert(PRINT_POPUP_BLOCKED_MESSAGE);
    }
  }, [orderNumber]);

  // Handle finish
  const handleFinish = useCallback(() => {
    finish();
    setIsCompleted(false);
    setOrderNumber('');
    setBranchReceiptNumber(null);
    setTentativeBranchReceiptCode(null);
    setOrderCompletedAt('');
    setShowPrintModal(false);
  }, [finish]);

  // Close print modal
  const handleClosePrintModal = useCallback(() => {
    setShowPrintModal(false);
  }, []);

  // Calculate progress percentage
  const progressPercentage = useMemo(
    () => (currentStep / totalSteps) * 100,
    [currentStep, totalSteps],
  );

  // Get step title
  const stepTitle = useMemo(() => {
    if (SALES_CUSTOMER_STEP_ENABLED) {
      switch (currentStep) {
        case 1:
          return 'Seleccionar piezas';
        case 2:
          return 'Descuentos y Métodos de Pago';
        case 3:
          return 'Cliente';
        case 4:
          return mode === 'edit' ? 'Confirmar cambios' : 'Confirmación de Venta';
        default:
          return 'Seleccionar piezas';
      }
    }

    switch (currentStep) {
      case 1:
        return 'Seleccionar piezas';
      case 2:
        return 'Descuentos y Métodos de Pago';
      case 3:
        return mode === 'edit' ? 'Confirmar cambios' : 'Confirmación de Venta';
      default:
        return 'Seleccionar piezas';
    }
  }, [currentStep, mode]);

  // Format grand total
  const formattedGrandTotal = useMemo(
    () => formatCurrency(calculateOrderSummary?.grandTotal ?? 0),
    [calculateOrderSummary],
  );

  const formattedOrderCompletedAt = useMemo(
    () => (orderCompletedAt ? formatDate(orderCompletedAt) : ''),
    [orderCompletedAt],
  );

  // Handle close confirmation modal
  const handleCloseConfirmation = useCallback(() => {
    setShowCancelConfirmation(false);
  }, []);

  return {
    // State
    isOpen,
    currentStep,
    isProcessing,
    isCompleted,
    orderNumber,
    branchReceiptNumber,
    branchReceiptChanged,
    tentativeBranchReceiptCode,
    orderCompletedAt,
    formattedOrderCompletedAt,
    showPrintModal,
    showCancelConfirmation,
    totalSteps,
    selectedLocation,
    selectedProducts,
    selectedCustomer,
    progressPercentage,
    stepTitle,
    formattedGrandTotal,
    calculateOrderSummary,
    isEditMode: mode === 'edit',
    editingOrderCode,
    // Handlers
    handleOrderCompleted,
    handleClose,
    handleCloseClick,
    cancelOperation,
    handleCloseConfirmation,
    handlePrintReceipt,
    handlePrint,
    handleFinish,
    handleClosePrintModal,
  };
};
